"use client";

import React from "react";
import { createPortal } from "react-dom";
import { STARTER_ONBOARDING_STEPS } from "./steps";
import { useOnboarding } from "./OnboardingContext";
import { cn } from "@/lib/utils";
import { isFeatureEnabled } from "@/utils/featuresUtil";
import { useTenancy } from "@/contexts/TenancyContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faXmark, faLock, faCircle, faPenToSquare } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import DuckIcon from "@/assets/ducks/unknown.svg";

/**
 * Sidebar-embedded onboarding progress widget.
 * Shows progress and allows users to navigate through onboarding steps.
 * Steps panel pops out to the side.
 */
export function SidebarOnboarding() {
  const { statuses, canComplete, openStep, setStatus, activeStep, closeActive, loading, presence } = useOnboarding();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [shouldRender, setShouldRender] = React.useState(false);
  const [lockedStep, setLockedStep] = React.useState<string | null>(null);
  const [panelPosition, setPanelPosition] = React.useState({ top: 0, left: 0 });
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  // Update panel position when opening
  const updatePanelPosition = React.useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPanelPosition({
        top: rect.top,
        left: rect.right + 12,
      });
    }
  }, []);

  // Handle open/close with animation
  const handleToggle = () => {
    if (isOpen) {
      // Closing: animate out then unmount
      setIsAnimating(false);
      setTimeout(() => {
        setIsOpen(false);
        setShouldRender(false);
      }, 300);
    } else {
      // Opening: calculate position, mount then animate in
      updatePanelPosition();
      setIsOpen(true);
      setShouldRender(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    }
  };

  const total = STARTER_ONBOARDING_STEPS.length;
  const done = STARTER_ONBOARDING_STEPS.filter(s => statuses[s.key] === "done").length;
  const progress = Math.round((done / total) * 100);

  // Find the next incomplete step that can be started
  const nextStep = React.useMemo(() => {
    return STARTER_ONBOARDING_STEPS.find(s => statuses[s.key] !== "done" && canComplete(s.key));
  }, [statuses, canComplete]);

  // Rotating encouragement messages - first one is always "Continue: X", rest are random
  const continueMessage = nextStep ? `Continue: ${nextStep.title}` : "Keep going!";
  
  // Messages for when no steps have been started yet
  const gettingStartedMessages = React.useMemo(() => [
    "Getting Started",
    `${total} steps to go`,
    "Let's begin!",
    "Start your journey",
  ], [total]);
  
  const randomMessages = React.useMemo(() => [
    `${total - done} steps remaining`,
    "You're making progress!",
    `${progress}% complete`,
    "Almost there!",
  ], [total, done, progress]);

  const [messageIndex, setMessageIndex] = React.useState(-1); // -1 = show continue message first

  // Get current message - first time shows continue, then random from remaining
  const currentMessage = React.useMemo(() => {
    if (done === 0) {
      // Use getting started messages when no steps completed
      if (messageIndex === -1) return gettingStartedMessages[0];
      return gettingStartedMessages[messageIndex % gettingStartedMessages.length];
    }
    if (messageIndex === -1) return continueMessage;
    return randomMessages[messageIndex];
  }, [messageIndex, continueMessage, randomMessages, done, gettingStartedMessages]);
  const [typedText, setTypedText] = React.useState("");
  const [isTyping, setIsTyping] = React.useState(false);

  // Get the target text - now always uses currentMessage which handles both states
  const targetText = currentMessage;

  // Typewriter effect
  React.useEffect(() => {
    if (typedText === targetText) return;
    
    setIsTyping(true);
    
    // If we need to delete (switching messages), delete first then type
    if (typedText.length > 0 && !targetText.startsWith(typedText)) {
      // Delete characters
      const deleteTimer = setTimeout(() => {
        setTypedText(prev => prev.slice(0, -1));
      }, 20 + Math.random() * 15); // Fast delete
      return () => clearTimeout(deleteTimer);
    }
    
    // Type characters
    if (typedText.length < targetText.length) {
      const typeTimer = setTimeout(() => {
        setTypedText(targetText.slice(0, typedText.length + 1));
      }, 30 + Math.random() * 40); // Variable typing speed for realism
      return () => clearTimeout(typeTimer);
    }
    
    setIsTyping(false);
  }, [typedText, targetText]);

  // Rotate messages every 5 seconds (give time to read + type)
  // First shows "Continue: X" or "Getting Started", then picks random from the rest
  React.useEffect(() => {
    const messagesLength = done === 0 ? gettingStartedMessages.length : randomMessages.length;
    const interval = setInterval(() => {
      setMessageIndex(currentIdx => {
        if (currentIdx === -1) {
          // First rotation: pick a random message (skip index 0 for getting started)
          return done === 0 ? 1 : Math.floor(Math.random() * messagesLength);
        }
        // Subsequent rotations: pick a different random message
        let newIdx;
        do {
          newIdx = Math.floor(Math.random() * messagesLength);
        } while (newIdx === currentIdx && messagesLength > 1);
        return newIdx;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [done, randomMessages.length, gettingStartedMessages.length]);

  // Initialize typed text
  React.useEffect(() => {
    setTypedText(targetText);
  }, []); // Only on mount
  
  const titleByKey = React.useMemo(() => {
    const m: Record<string, string> = {};
    for (const s of STARTER_ONBOARDING_STEPS) m[s.key] = s.title;
    return m;
  }, []);

  // Get the locked step details for the dialog
  const lockedStepDetails = React.useMemo(() => {
    if (!lockedStep) return null;
    const step = STARTER_ONBOARDING_STEPS.find(s => s.key === lockedStep);
    if (!step) return null;
    const missingDeps = (step.dependsOn || []).filter(depKey => statuses[depKey] !== "done");
    return {
      step,
      missingDeps: missingDeps.map(key => ({
        key,
        title: titleByKey[key] || key,
        status: statuses[key],
        canStart: canComplete(key) // Check if this dependency can be started
      }))
    };
  }, [lockedStep, statuses, titleByKey, canComplete]);

  // Close panel when clicking outside (with animation)
  React.useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-onboarding-panel]') && !target.closest('[data-onboarding-trigger]')) {
        // Animate out
        setIsAnimating(false);
        setTimeout(() => {
          setIsOpen(false);
          setShouldRender(false);
        }, 300);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Get the active tenancy plan
  const { activeTenancy } = useTenancy();
  const currentPlan = activeTenancy?.plan?.toLowerCase() || '';
  
  // Determine which plan the user is on
  const isStarterPlan = currentPlan.includes('starter') || currentPlan === '' || currentPlan === 'loading...';
  const isBusinessPlan = currentPlan.includes('business') || currentPlan === 'iso';
  const isEnterprisePlan = currentPlan.includes('enterprise');
  
  // Check feature flags for each plan's onboarding
  const hasStarterOnboarding = isFeatureEnabled('Starter Plan Onboarding');
  const hasBusinessOnboarding = isFeatureEnabled('Onboarding Business Plan');
  const hasEnterpriseOnboarding = isFeatureEnabled('Onboarding Enterprise Plan');
  
  // Only show onboarding if the user's plan has its corresponding feature enabled
  // - Starter plan users see onboarding if 'Starter Plan Onboarding' is enabled
  // - Business plan users see onboarding if 'Onboarding Business Plan' is enabled
  // - Enterprise plan users see onboarding if 'Onboarding Enterprise Plan' is enabled
  const shouldShowOnboarding = 
    (isStarterPlan && hasStarterOnboarding) ||
    (isBusinessPlan && hasBusinessOnboarding) ||
    (isEnterprisePlan && hasEnterpriseOnboarding);
  
  if (!shouldShowOnboarding) return null;

  // Don't render if all steps are done
  if (done === total) return null;

  return (
    <div className="w-full px-2 py-1.5 relative">
      {/* Trigger button - subtle sidebar style */}
      <button
        ref={triggerRef}
        data-onboarding-trigger
        onClick={handleToggle}
        className={cn(
          "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md transition-all duration-200",
          "bg-white/5 hover:bg-white/10",
          "border border-white/10 hover:border-white/20",
          "text-left group",
          isOpen && "bg-white/10 border-white/20"
        )}
      >
        {/* Icon */}
        <div className="relative h-6 w-6 shrink-0 rounded bg-white/10 flex items-center justify-center">
          <img src={DuckIcon} alt="Duck" className="h-4 w-4" />
          {/* Subtle indicator when there's progress to make */}
          {done < total && (
            <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-primary/80" />
          )}
        </div>

        {/* Text content */}
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-white/80 truncate">
            {typedText}
            {isTyping && <span className="inline-block w-0.5 h-3 bg-white/60 ml-0.5 animate-pulse" />}
          </div>
          {/* Progress bar */}
          <div className="mt-1 h-0.5 w-full bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-linear-to-r from-primary/20 to-primary/90 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Progress text */}
        <div className="text-[10px] text-white/40 font-medium shrink-0">
          {done}/{total}
        </div>
      </button>

      {/* Pop-out panel with genie animation - rendered via portal to escape stacking context */}
      {shouldRender && createPortal(
        <div 
          data-onboarding-panel
          className={cn(
            "fixed z-[9999] w-[380px] rounded-2xl bg-card/95 backdrop-blur-xl border border-white/10 shadow-2xl overflow-hidden",
            "transition-all duration-300 ease-out",
            isAnimating 
              ? "opacity-100 scale-100 translate-x-0" 
              : "opacity-0 scale-75 -translate-x-4"
          )}
          style={{ 
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)',
            transformOrigin: 'left center',
            top: panelPosition.top,
            left: panelPosition.left,
          }}
        >
          {/* Header */}
          <div className="px-5 py-4 border-b border-white/5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-foreground">Onboarding Steps</h3>
                <p className="text-xs text-muted-foreground mt-0.5">{done} of {total} complete</p>
              </div>
              <button 
                onClick={handleToggle}
                className="h-7 w-7 rounded-lg hover:bg-white/5 flex items-center justify-center transition-colors"
              >
                <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
            {/* Progress bar */}
            <div className="mt-3 h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
              <div 
                className="h-full bg-linear-to-r from-primary/40 to-primary/90 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Steps list */}
          <div className="max-h-[55vh] overflow-y-auto py-2">
            {loading ? (
              <div className="px-5 py-8 text-center text-sm text-muted-foreground">
                Loading...
              </div>
            ) : (
              STARTER_ONBOARDING_STEPS.map((step, index) => {
                const status = statuses[step.key];
                const depsOk = canComplete(step.key);
                const activeUsers = presence[step.key] || [];
                const isLast = index === STARTER_ONBOARDING_STEPS.length - 1;
                
                return (
                  <div 
                    key={step.key} 
                    className={cn(
                      "px-5 py-3 flex items-start gap-3 hover:bg-white/[0.02] transition-colors",
                      !isLast && "border-b border-white/[0.03]"
                    )}
                  >
                    {/* Status indicator */}
                    <div className="pt-0.5">
                      {status === "done" ? (
                        <div className="h-5 w-5 rounded-full bg-emerald-500/20 flex items-center justify-center">
                          <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3 text-emerald-500" />
                        </div>
                      ) : !depsOk ? (
                        <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center animate-pulse">
                          <FontAwesomeIcon icon={faCircle} className="h-3 w-3 text-muted-foreground/50" />
                        </div>
                      ) : status === "in_progress" ? (
                        <div className="h-5 w-5 rounded-full bg-blue-500/20 flex items-center justify-center relative">
                          <span className="absolute inset-0 rounded-full bg-blue-500/20 animate-ping opacity-75" style={{ animationDuration: '2s' }} />
                          <div className="h-2 w-2 rounded-full bg-blue-500 relative" />
                        </div>
                      ) : (
                        <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center relative">
                          <span className="absolute inset-0 rounded-full bg-primary/15 animate-ping opacity-60" style={{ animationDuration: '2s' }} />
                          <FontAwesomeIcon icon={faCircle} className="h-3 w-3 text-primary/50 relative" />
                        </div>
                      )}
                    </div>

                    {/* Step info */}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground/90 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span>{step.title}</span>
                        {/* Live presence indicator */}
                        {activeUsers.length > 0 && (
                          <span 
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-medium"
                            title={activeUsers.map(u => u.userName).join(', ')}
                          >
                            <span className="relative flex h-1.5 w-1.5">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                            </span>
                            {activeUsers.length === 1 
                              ? activeUsers[0].userName.split(' ')[0]
                              : `${activeUsers.length}`
                            }
                          </span>
                        )}
                      </div>
                      {step.description && (
                        <div className="text-xs text-muted-foreground mt-0.5">{step.description}</div>
                      )}
                    </div>

                    {/* Action button */}
                    <div className="shrink-0 flex items-center gap-1.5">
                      {status !== "done" ? (
                        depsOk ? (
                          <button
                            onClick={() => openStep(step.key)}
                            className="text-xs px-3 py-1 rounded-full font-medium transition-colors border border-primary/25 text-primary hover:bg-primary/10 hover:border-primary"
                          >
                            {status === "in_progress" ? "Resume" : "Start"}
                          </button>
                        ) : (
                          <button
                            onClick={() => setLockedStep(step.key)}
                            className="h-7 w-7 rounded-full flex items-center justify-center bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                            title="This step has requirements"
                          >
                            <FontAwesomeIcon icon={faLock} className="h-3.5 w-3.5" />
                          </button>
                        )
                      ) : (
                        <>
                          <span className="text-xs text-emerald-600 dark:text-emerald-500 flex items-center gap-1">
                            <FontAwesomeIcon icon={faCircleCheck} className="h-3.5 w-3.5" /> Done
                          </span>
                          <button
                            onClick={() => {
                              setStatus(step.key, "in_progress");
                              openStep(step.key);
                            }}
                            className="text-xs px-3 py-1 rounded-full font-medium border border-amber-500/25 text-amber-600/50 dark:text-amber-500 hover:bg-amber-500/10 hover:border-amber-500 transition-colors flex items-center gap-1"
                            title="Redo this step"
                          >
                            <FontAwesomeIcon icon={faPenToSquare} className="h-3 w-3" /> &nbsp; Edit
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {activeStep && (
            <div className="px-5 py-3 border-t border-white/5 bg-white/[0.02]">
              <button 
                onClick={closeActive} 
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors"
              >
                <FontAwesomeIcon icon={faXmark} className="h-3 w-3" /> Close current modal
              </button>
            </div>
          )}
        </div>,
        document.body
      )}

      {/* Locked Step Dialog */}
      <Dialog open={!!lockedStep} onOpenChange={(open) => !open && setLockedStep(null)}>
        <DialogContent className="sm:max-w-md bg-card/95 backdrop-blur-xl border-white/10">
          {/* Close button */}
          <button
            onClick={() => setLockedStep(null)}
            className="absolute right-4 top-4 h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
          </button>
          
          <div className="flex flex-col items-center text-center py-4">
            {/* Lock Icon */}
            <div className="h-16 w-16 rounded-full bg-amber-500/10 flex items-center justify-center mb-4">
              <FontAwesomeIcon icon={faLock} className="h-8 w-8 text-amber-500" />
            </div>
            
            {/* Title */}
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Step Locked
            </h2>
            
            {/* Description */}
            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
              Complete the following steps first to unlock <span className="font-medium text-foreground">{lockedStepDetails?.step.title}</span>
            </p>

            {/* Required Steps */}
            <div className="w-full space-y-2 mb-6">
              {lockedStepDetails?.missingDeps.map((dep) => (
                <div 
                  key={dep.key}
                  className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border"
                >
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    {!dep.canStart ? (
                      <FontAwesomeIcon icon={faLock} className="h-4 w-4 text-muted-foreground" />
                    ) : dep.status === "in_progress" ? (
                      <div className="h-3 w-3 rounded-full bg-blue-500 animate-pulse" />
                    ) : (
                      <FontAwesomeIcon icon={faCircle} className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-sm font-medium text-foreground">{dep.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {!dep.canStart ? "Also locked" : dep.status === "in_progress" ? "In progress" : "Not started"}
                    </div>
                  </div>
                  {dep.canStart ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0 border-primary/25 text-primary hover:bg-primary/10 hover:border-primary"
                      onClick={() => {
                        setLockedStep(null);
                        openStep(dep.key);
                      }}
                    >
                      {dep.status === "in_progress" ? "Continue" : "Start"}
                    </Button>
                  ) : (
                    <button
                      onClick={() => setLockedStep(dep.key)}
                      className="h-8 w-8 rounded-full flex items-center justify-center bg-muted hover:bg-muted/80 text-muted-foreground transition-colors"
                      title="This step also has requirements"
                    >
                      <FontAwesomeIcon icon={faLock} className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Close Button */}
            <Button
              variant="ghost"
              onClick={() => setLockedStep(null)}
              className="text-muted-foreground"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
